import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import Flexbox from "flexbox-react";
import { Card, Button, Icon, Collapse } from "@blueprintjs/core";
import { FilledButton, OutlinedButton } from "../buttons-20250224T111359Z-001/buttons";
import ToastStore from "../../stores/ToastStore";
import CheckInDialog from "./CheckInDialog";
import EditIcon from "../../assets/editIcon.svg";
import Delete from "../../assets/Delete.svg";
import StepDrawer from "./StepDrawer";
import TaskDrawer from "./TaskDrawer";
import ConfirmationDialog from "../../views/ConfirmationDialog";
import { AnchorButton, Tooltip, Position } from "@blueprintjs/core";

const StepsManager = () => {
  const emptyCheckinForm = {
    id: null,
    name: "",
    goal_setting_id: null,
    goal_title: "",
  };
  const emptyStepForm = {
    id: null,
    name: "",
    description: "",
    selectedJobProfile: null
  };
  const emptyTaskForm = {
    id: null,
    name: "",
    description: "",
    due_date: "",
    checklist: false,
    //  is_employee_attachment: false,
    attachment: null
  };

  const [isLoading, setIsLoading] = useState(false);
  const [checkinSteps, setCheckinSteps] = useState([]);
  const [jobProfiles, setJobProfiles] = useState([]);
  const [goalCycles, setGoalCycles] = useState([]);

  const [selectedCheckin, setSelectedCheckin] = useState(null);
  const [selectedStep, setSelectedStep] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [sendingNotificationTaskId, setSendingNotificationTaskId] = useState(null);

  const [isDeleteCheckinDialogOpen, setIsDeleteCheckinDialogOpen] = useState(false);
  const [isDeleteStepDialogOpen, setIsDeleteStepDialogOpen] = useState(false);
  const [isDeleteTaskDialogOpen, setIsDeleteTaskDialogOpen] = useState(false);
  const [selectedCheckinToDelete, setSelectedCheckinToDelete] = useState(null);
  const [selectedStepToDelete, setSelectedStepToDelete] = useState(null);
  const [selectedTaskToDelete, setSelectedTaskToDelete] = useState(null);

  const [checkinForm, setCheckinForm] = useState(emptyCheckinForm);
  const [stepForm, setStepForm] = useState(emptyStepForm);
  const [taskForm, setTaskForm] = useState(emptyTaskForm);
  const [isStepDrawerOpen, setIsStepDrawerOpen] = useState(false);
  const [isTaskDrawerOpen, setIsTaskDrawerOpen] = useState(false);
  const [isAddCheckInDialogOpen, setIsAddCheckInDialogOpen] = useState(false);
  const [openCheckinId, setOpenCheckinId] = useState(null);
  const contentRefs = useRef({});
  useEffect(() => {
    fetchJobProfiles();
    fetchCheckInStepsTasksNames();
    getAllGoalCycles();
  }, []);

  const openAddCheckInDialog = (checkin) => {
    setCheckinForm(checkin);
    setIsAddCheckInDialogOpen(true);
  };

  const openStepDrawer = (step) => {
    setStepForm(step);
    setIsStepDrawerOpen(true);
  };

  const openTaskDrawer = (task) => {
    setTaskForm(task);
    setIsTaskDrawerOpen(true);
  };

  const closeAddCheckInDialog = () => {
    setIsAddCheckInDialogOpen(false);
    setCheckinForm(emptyCheckinForm);
  };

  const closeStepDrawer = () => {
    setIsStepDrawerOpen(false);
    setStepForm(emptyStepForm);
  };

  const closeTaskDrawer = () => {
    setIsTaskDrawerOpen(false);
    setTaskForm(emptyTaskForm);
  };

  const fetchJobProfiles = () => {
    axios.get(`${Cookies.get("apiEnv")}/job_profiles`)
      .then(response => {
        console.log("job profiles", response.data);
        const profiles = Array.isArray(response.data) ? response.data : [];
        setJobProfiles(profiles);
      })
      .catch(error => {
        console.error("Error fetching job profiles", error);
      });
  }

  const getAllGoalCycles = () => {
    axios.get(`${Cookies.get("apiEnv")}/goal_settings`)
      .then(response => {
        console.log("goal cycles", response.data);
        const goalCycles = Array.isArray(response.data) ? response.data : [];
        setGoalCycles(goalCycles);
      })
      .catch(error => {
        console.error("Error fetching goal cycles", error);
      });
  };

  // Fetch Checkins + Steps
  const fetchCheckInStepsTasksNames = async (newCheckinId = null) => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${Cookies.get("apiEnv")}/onboarding_steps/steps_with_tasks`);
      const formatted = response.data.map((item) => ({
        id: item.checkin?.id,
        name: item.checkin?.name,
        goal_title: item.checkin?.goal_title,
        goal_setting_id: item.checkin?.goal_setting_id,
        steps: (item.steps || []).map((step) => ({
          id: step.id,
          name: step.name,
          tasks: (step.onboarding_tasks || []).map((t) => ({ id: t.id, name: t.name })),
        })),
      }));
      setCheckinSteps(formatted);
      //Case 1: If new checkin created → select it
      if (newCheckinId) {
        const newCheckin = formatted.find(c => c.id === newCheckinId);
        if (newCheckin) {
          setSelectedCheckin(newCheckin);
          if (newCheckin.steps?.length) {
            const firstStep = newCheckin.steps[0];
            setSelectedStep(firstStep);
            fetchStepDataWithTasks(firstStep.id);
          } else {
            setSelectedStep(null);
          }
        }
        return;
      }
      // Case 2: First load (no selection yet)
      if (!selectedCheckin && formatted.length > 0) {
        const first = formatted[0];
        setSelectedCheckin(first);
        setSelectedStep(first.steps[0]);
        if (first.steps?.length) { fetchStepDataWithTasks(first.steps[0].id); }
        return;
      }
      // Case 3: Preserve selected checkin
      if (selectedCheckin) {
        const updated = formatted.find((c) => c.id === selectedCheckin.id);
        if (updated) {
          setSelectedCheckin(updated);
          // also keep selected step if exists
          if (selectedStep) {
            const updatedStep = updated.steps.find((s) => s.id === selectedStep.id);
            if (updatedStep) {
              fetchStepDataWithTasks(updatedStep.id);
            } else if (updated.steps.length > 0) {
              fetchStepDataWithTasks(updated.steps[0].id);
            } else {
              setSelectedStep(null);
            }
          }
        } else {
          // fallback if checkin deleted
          const first = formatted[0];
          setSelectedCheckin(first || null);
          if (first?.steps?.length) { fetchStepDataWithTasks(first.steps[0].id); }
          else { setSelectedStep(null); }
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  //  Fetch Step Details
  const fetchStepDataWithTasks = async (stepId) => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${Cookies.get("apiEnv")}/onboarding_steps/fetch_tasks/${stepId}`);
      setSelectedStep({
        id: res.data.step.id,
        name: res.data.step.name,
        description: res.data.step.description,
        job_profile_id: res.data.step.job_profile_id,
        tasks: res.data.tasks.map(task => ({
          id: task.id,
          name: task.name,
          description: task.description,
          due_date: task.due_date,
          due_count: task.duration_count,
          due_unit: task.duration_type,
          checklist: task.checklist,
          is_employee_attachment: task.is_employee_attachment,
          attachment: task.attachment
        }))
      });
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckinSelect = (checkin) => {
    setOpenCheckinId((prev) => {
      //CASE 1: clicking same → CLOSE
      if (prev === checkin.id) {
        setSelectedCheckin(null);
        setSelectedStep(null);
        return null;
      }
      // CASE 2: opening new checkin
      setSelectedCheckin(checkin);
      if (checkin.steps?.length) {
        const firstStep = checkin.steps[0];
        setSelectedStep(firstStep);
        fetchStepDataWithTasks(firstStep.id);
      } else {
        setSelectedStep(null);
      }

      return checkin.id;
    });
  };

  const sendTaskNotification = async (taskId, stepId) => {
    try {
      setSendingNotificationTaskId(taskId);
  
      await axios.post(
        `${Cookies.get("apiEnv")}/checkins/send_notification`,
        {
          task_id: taskId,
          step_id: stepId
        }
      );
  
      ToastStore.showToast(
      " email sent successfully.",
        "success"
      );
    } catch (error) {
      console.error(error);
  
      ToastStore.showToast(
        "Failed to send notification email.",
        "danger"
      );
    } finally {
      setSendingNotificationTaskId(null);
    }
  };

  const saveCheckIn = async (form) => {
    console.log("checkin form", form);
    setIsLoading(true);
    try {
      let response;
      if (form.id) {
        console.log("update checkin", form);
        response = await axios.put(`${Cookies.get("apiEnv")}/checkins/update/${form.id}`, {
          title: form.name,
          goal_setting_id: form.goal_setting_id,
        });
      } else {
        response = await axios.post(`${Cookies.get("apiEnv")}/checkins/create`, {
          title: form.name,
          goal_setting_id: form.goal_setting_id,
        });
      }
      const newCheckinId = response.data?.id;
      ToastStore.showToast("Check-in created", "success");
      await fetchCheckInStepsTasksNames(newCheckinId);
      setIsAddCheckInDialogOpen(false)
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const saveStep = async (form) => {
    try {
      let response;
      if (form.id) {
        response = await axios.put(`${Cookies.get("apiEnv")}/onboarding_steps/${form.id}`,
          {
            name: form.name,
            description: form.description,
            assigned_to: form.assigned_to,
            job_profile_id: form.job_profile_id,
            checkin_id: selectedCheckin.id
          }
        );
      } else {
        const newStep = {
          name: form.name,
          description: form.description,
          assigned_to: form.assigned_to,
          job_profile_id: form.job_profile_id,
          checkin_id: selectedCheckin.id
        };
        response = await axios.post(`${Cookies.get("apiEnv")}/onboarding_steps`, newStep);
      }
      fetchCheckInStepsTasksNames();

      const savedStep = response.data;
      fetchStepDataWithTasks(savedStep.id);

      setIsStepDrawerOpen(false);
    } catch (error) {
      console.error("Error saving step", error);
    }
  };

  const saveTask = async (form) => {
    const formData = new FormData();

    formData.append("name", form.name);
    formData.append("description", form.description);
    formData.append("onboarding_step_id", selectedStep.id);
    formData.append("duration_count", form.due_count);
    formData.append("duration_type", form.due_unit);

    if (form.attachment) {
      formData.append("attachment", form.attachment);
    }

    try {
      let response;

      if (form.id) {
        response = await axios.put(
          `${Cookies.get("apiEnv")}/onboarding_tasks/${form.id}`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      } else {
        response = await axios.post(
          `${Cookies.get("apiEnv")}/onboarding_tasks`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      }

      // After save → refresh data
      fetchCheckInStepsTasksNames();
      fetchStepDataWithTasks(selectedStep.id);
      setIsTaskDrawerOpen(false);

    } catch (error) {
      console.error("Error saving task", error);
    }
  };
  const deleteCheckin = async (checkinId) => {
    try {
      await axios.delete(`${Cookies.get("apiEnv")}/checkins/${checkinId}`);
      ToastStore.showToast("Check-in deleted", "success");
      //handle UI state after delete
      if (selectedCheckin?.id === checkinId) {
        setSelectedCheckin(null);
        setSelectedStep(null);
      }
      fetchCheckInStepsTasksNames();

    } catch (error) {
      console.error(error);
      ToastStore.showToast("Failed to delete check-in", "danger");
    } finally {
      setIsDeleteCheckinDialogOpen(false);
    }
  };

  const deleteStep = async (stepId) => {
    try {
      await axios.delete(`${Cookies.get("apiEnv")}/onboarding_steps/${stepId}`);
      //  If deleted step is selected → clear right panel
      if (selectedStep?.id === stepId) {
        setSelectedStep(null);
      }
      setIsDeleteStepDialogOpen(false);
      fetchCheckInStepsTasksNames();
      ToastStore.showToast("Step deleted", "success");
    } catch (error) {
      console.error("Error deleting step", error);
      ToastStore.showToast("Failed to delete step", "danger");
    }
  };

  const deleteTask = async (taskId) => {
    try {
      await axios.delete(`${Cookies.get("apiEnv")}/onboarding_tasks/${taskId}`);
      // Update selectedStep locally (instant UI update)
      setSelectedStep((prev) => ({
        ...prev,
        tasks: prev.tasks.filter((t) => t.id !== taskId),
      }));
      setIsDeleteTaskDialogOpen(false);
      fetchCheckInStepsTasksNames();
      ToastStore.showToast("Task deleted", "success");
    } catch (error) {
      console.error("Error deleting task", error);
      ToastStore.showToast("Failed to delete task", "danger");
    }
  };

  return (
    <Flexbox flexGrow={1} flexDirection="column" gap="12px" width="100%">
      {/* HEADER */}
      <Flexbox display="flex" justifyContent="flex-end" className="push-10-b">
        <FilledButton text="Add New Check-In" onClick={() => openAddCheckInDialog()} />
      </Flexbox>

      <Flexbox display="flex" flexDirection="column" gap="12px" width="100%">
        {checkinSteps?.length > 0 && (
          <>
            {checkinSteps.map((checkin) => {
              const isActive = selectedCheckin?.id === checkin.id;

              return (
                <Card
                  key={checkin.id}
                  style={{
                    borderRadius: 12,
                    marginBottom: 12,
                    border: "1px solid #e6eaf0",
                    background: isActive ? "#fff" : undefined,
                    boxShadow: isActive
                      ? "0 4px 12px rgba(0,0,0,0.06)"
                      : "0 1px 3px rgba(0,0,0,0.04)"
                  }}
                >
                  <Flexbox display="flex" flexDirection="column" gap="12px">
                    {/* 🔹 HEADER */}
                    <div
                      onClick={() => { handleCheckinSelect(checkin); }}
                      className="step-card-header"
                    >
                      <div>
                        <Flexbox flexDirection="row" gap="12px" alignItems="center">
                          <div style={{ fontWeight: 600, fontSize: 20 }}>
                            {checkin.name}
                          </div>
                          {checkin.goal_title && (
                            <div
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                padding: "3px 10px",
                                borderRadius: "999px",
                                background: "#EEF6FF",
                                color: "#106BA3",
                                border: "1px solid #BFDEFF",
                                fontSize: "12px",
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                                marginLeft: 10,
                              }}
                            >
                              {checkin.goal_title}
                            </div>
                          )}
                        </Flexbox>
                        <div style={{ fontSize: 13, color: "#888" }}>
                          {checkin.steps.length} Steps
                        </div>
                      </div>
                      {/* Action-buttons */}
                      <Flexbox className="step-card-header-actions">
                        <div className="step-card-header-icons">
                          <Button
                            icon={<img src={EditIcon} style={{ width: 18, height: 18 }} />}
                            minimal
                            onClick={(e) => {
                              e.stopPropagation();
                              openAddCheckInDialog(checkin);
                            }}
                          />
                          <Button
                            icon={<img src={Delete} style={{ width: 18, height: 18 }} />}
                            minimal
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsDeleteCheckinDialogOpen(true);
                              setSelectedCheckinToDelete(checkin);
                            }}
                          />
                        </div>
                        <div style={{ marginTop: 5, transition: "0.3s" }}
                        >
                          <Icon icon={isActive ? "chevron-down" : "chevron-right"} color="#5f6b7c" size={18} />
                        </div>
                      </Flexbox>

                    </div>

                    {/* 🔹 EXPANDED CONTENT */}
                    <Collapse isOpen={openCheckinId === checkin.id}>
                      <div className="step-card-content" >
                        {/* LEFT: Steps */}
                        <div className="step-card-content-left">
                          <div className="step-card-content-left-steps">
                            {/* Vertical line */}
                            <div className="step-card-content-vertical-line" />
                            {checkin.steps.map((step) => (
                              <div key={step.id} style={{ marginBottom: 8, position: "relative" }}>
                                <div
                                  onClick={() => fetchStepDataWithTasks(step.id)}
                                  className="step-card-content-left-step"
                                  style={{
                                    background: selectedStep?.id === step.id
                                      ? "#e7f0ff"
                                      : "transparent"
                                  }}
                                >
                                  <div
                                    className="step-card-step-circle"
                                    style={{ background: selectedStep?.id == step.id ? "#106ba3" : "#cbd5e1" }}
                                  />
                                  <Flexbox justifyContent="space-between" style={{ width: "100%" }}>
                                    <span style={{ fontSize: 14 }}>{step.name}</span>
                                    <span className="step-card-task-count">
                                      {step.tasks.length}
                                    </span>
                                  </Flexbox>
                                </div>

                                {/* Tasks (preview) */}
                                {selectedStep?.id === step.id &&
                                  step.tasks.map((task) => (
                                    <div key={task.id} className="step-card-sidebar-task" style={{ color: '#4d4d4d' }} >
                                      <div className="step-card-sidebar-task-circle" />
                                      <span>{task.name}</span>
                                    </div>
                                  ))}
                              </div>
                            ))}

                          </div>
                          <Flexbox display="flex" justifyContent="flex-start">
                            <FilledButton text="+ Add Step" onClick={() => openStepDrawer(null)} style={{ marginTop: 10, marginLeft: 10 }} />
                          </Flexbox>
                        </div>

                        {/* RIGHT: Tasks Detail */}
                        <Flexbox flex={1} flexDirection="column" gap="12px" width="100%">
                          {selectedStep ? (
                            <Flexbox flexDirection="column" gap="12px">
                              <Flexbox flexDirection="row" gap="12px" justifyContent="space-between" alignItems="center" className="push-20-b">
                                <Flexbox flexDirection="column" gap="12px" justifyContent="flex-start">
                                  <div style={{ fontWeight: 600, fontSize: 16 }}>
                                    {selectedStep?.name}
                                  </div>
                                  <div style={{ fontSize: 13, color: "#888" }}>
                                    {selectedStep?.description}
                                  </div>
                                </Flexbox>
                                <Flexbox flexDirection="row" gap="12px" className="push-10-r">
                                  <Button icon={<img src={EditIcon} alt="Edit Icon" style={{ width: 18, height: 18 }} />}
                                    minimal={true}
                                    onClick={() => openStepDrawer(selectedStep)} />
                                  <Button icon={<img src={Delete} alt="Delete Icon" style={{ width: 18, height: 18 }} />}
                                    minimal={true}
                                    onClick={() => { setIsDeleteStepDialogOpen(true); setSelectedStepToDelete(selectedStep); }} />
                                </Flexbox>
                              </Flexbox>

                              {selectedStep?.tasks?.map((task) => (
                                <Card
                                  key={task.id}
                                  style={{
                                    marginBottom: 10,
                                    padding: 12,
                                    borderRadius: 10
                                  }}
                                >
                                  <Flexbox flexDirection="row" gap="12px" justifyContent="space-between" alignItems="center">
                                    <Flexbox flexDirection="row" gap="12px" alignItems="center">
                                      <div style={{ fontWeight: 500, fontSize: 14 }}>{task.name}</div>
                                    </Flexbox>

                                    <Flexbox flexDirection="row" gap="12px" alignItems="center">
                                      <Tooltip
                                        position={Position.TOP}
                                        content={
                                          <div
                                            style={{
                                              maxWidth: "240px",
                                              whiteSpace: "normal",
                                              lineHeight: "1.5"
                                            }}
                                          >
                                            Sends an email notification to the assigned employee with this Checkin.
                                          </div>
                                        }
                                      >
                                        <div style={{ display: "inline-block" }}>
                                          <OutlinedButton
                                            text={
                                              sendingNotificationTaskId === task.id
                                                ? "Sending..."
                                                : "Send Reminder"
                                            }
                                            disabled={sendingNotificationTaskId === task.id}
                                            onClick={() => sendTaskNotification(task.id, selectedStep.id)}
                                          />
                                        </div>
                                      </Tooltip>
                                      <Button icon={<img src={EditIcon} alt="Edit Icon" style={{ width: 18, height: 18 }} />}
                                        minimal={true}
                                        onClick={() => openTaskDrawer(task)} />
                                      <Button icon={<img src={Delete} alt="Delete Icon" style={{ width: 18, height: 18 }} />}
                                        minimal={true}
                                        onClick={() => { setIsDeleteTaskDialogOpen(true); setSelectedTaskToDelete(task); }} />
                                    </Flexbox>
                                  </Flexbox>
                                </Card>
                              ))}
                              <Flexbox justifyContent="flex-start">
                                <FilledButton text="+ Add Task" onClick={() => openTaskDrawer(null)} />
                              </Flexbox>
                            </Flexbox>

                          ) : (
                            <div></div>
                          )}
                        </Flexbox>
                      </div>
                    </Collapse>

                  </Flexbox>
                </Card>
              );
            })}
          </>
        )}
      </Flexbox>

      {/* DIALOG */}
      {isAddCheckInDialogOpen && (
        <CheckInDialog
          isOpen={isAddCheckInDialogOpen}
          onClose={closeAddCheckInDialog}
          onSave={saveCheckIn}
          checkin={checkinForm}
          goalCycles={goalCycles}
        />
      )}
      {
        isTaskDrawerOpen && (
          <TaskDrawer
            isOpen={isTaskDrawerOpen}
            onClose={closeTaskDrawer}
            task={taskForm}
            onSave={saveTask}
          />
        )}
      {
        isStepDrawerOpen && (
          <StepDrawer
            isOpen={isStepDrawerOpen}
            jobProfiles={jobProfiles}
            onClose={closeStepDrawer}
            step={stepForm}
            onSave={saveStep}
          />
        )}

      <ConfirmationDialog
        isOpen={isDeleteStepDialogOpen}
        onClose={() => setIsDeleteStepDialogOpen(false)}
        onConfirm={() => deleteStep(selectedStepToDelete.id)}
        title="Delete Step"
        message="Are you sure you want to delete this step"
        confirmText="Delete"
        confirmIntent="danger"
      // isSubmitting={isDeleteStepSubmitting}
      />
      <ConfirmationDialog
        isOpen={isDeleteTaskDialogOpen}
        onClose={() => setIsDeleteTaskDialogOpen(false)}
        onConfirm={() => deleteTask(selectedTaskToDelete.id)}
        title="Delete Task"
        message="Are you sure you want to delete this task"
        //  itemName={selectedTaskToDelete.name}
        confirmText="Delete"
        confirmIntent="danger"
      />
      <ConfirmationDialog
        isOpen={isDeleteCheckinDialogOpen}
        onClose={() => setIsDeleteCheckinDialogOpen(false)}
        onConfirm={() => deleteCheckin(selectedCheckinToDelete.id)}
        title="Delete Check-In"
        message="Are you sure you want to delete this check-in"
        confirmText="Delete"
        confirmIntent="danger"
      />
    </Flexbox>
  );
};

export default StepsManager;